import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import FeatureUpsellPage from './ui/FeatureUpsellPage';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import PageHeader from './ui/PageHeader';
import {
  BarChart3,
  PlayCircle,
  ThumbsUp,
  Eye,
  Loader2,
  FileText,
  FileSpreadsheet,
  Zap,
  Clock,
  Calendar,
  Target,
  Package,
  ExternalLink,
  Award,
  Lightbulb,
  ArrowRight
} from 'lucide-react';
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const InsightsPage = ({ onLogout }) => {
  const { t } = useTranslation();
  usePageTitle(t('channelInsights.title'));
  const { theme } = useTheme();
  const { sidebarWidth, isMobile } = useSidebar();

  // Funcao para formatar numeros com separador de milhar
  const formatNumber = (num) => {
    return num?.toLocaleString('pt-BR') || '0';
  };

  // Funcao para filtrar dados do grafico por periodo
  const filterChartData = (data) => {
    if (!data || timeFilter === 'all') return data;

    const now = new Date();
    const cutoffDate = new Date();

    switch(timeFilter) {
      case '1m':
        cutoffDate.setMonth(now.getMonth() - 1);
        break;
      case '3m':
        cutoffDate.setMonth(now.getMonth() - 3);
        break;
      case '6m':
        cutoffDate.setMonth(now.getMonth() - 6);
        break;
      case '1y':
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return data;
    }

    return data.filter(item => new Date(item.date) >= cutoffDate);
  };

  // Estados
  const [channelUrl, setChannelUrl] = useState('');
  const [quantity, setQuantity] = useState(100);
  const [orderBy, setOrderBy] = useState('recent');
  const [analyzing, setAnalyzing] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [exportLoading, setExportLoading] = useState(null);
  const [accessCheck, setAccessCheck] = useState({ loading: true, hasAccess: false, message: '' });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Verificar acesso ao recurso (igual Videos Virais e Nichos Virais)
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/auth/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Check if super admin
        setIsSuperAdmin(response.data.is_super_admin || false);

        // Check insights_canal_acesso field (igual nichos_virais_acesso)
        setAccessCheck({
          loading: false,
          hasAccess: response.data.insights_canal_acesso || false,
          message: response.data.insights_canal_mensagem || t('channelInsights.resource_unavailable')
        });
      } catch (error) {
        console.error('Erro ao verificar acesso:', error);
        setAccessCheck({
          loading: false,
          hasAccess: false,
          message: t('channelInsights.error_check_access')
        });
        setIsSuperAdmin(false);
      }
    };

    checkAccess();
  }, []);

  // Polling para verificar progresso
  useEffect(() => {
    if (!jobId || !analyzing) return;

    const checkProgress = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/insights/status/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const { status, progress: prog, message } = response.data;

        setProgress(prog);
        setProgressMessage(message);

        if (status === 'completed') {
          // Buscar resultado
          const resultResponse = await axios.get(`${API}/insights/resultado/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          setAnalysisResult(resultResponse.data.data);
          setAnalyzing(false);
          toast.success(t('channelInsights.analysis_complete'));
        } else if (status === 'failed') {
          setAnalyzing(false);
          toast.error(message);
        }
      } catch (error) {
        console.error('Erro ao verificar progresso:', error);
      }
    };

    const interval = setInterval(checkProgress, 2000);
    return () => clearInterval(interval);
  }, [jobId, analyzing]);

  const handleStartAnalysis = async () => {
    if (!channelUrl.trim()) {
      toast.error(t('channelInsights.enter_url'));
      return;
    }

    try {
      setAnalyzing(true);
      setProgress(0);
      setProgressMessage(t('channelInsights.starting_analysis'));
      setAnalysisResult(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/insights/analisar`,
        { channel_url: channelUrl, quantity, order_by: orderBy },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setJobId(response.data.job_id);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Erro ao iniciar analise:', error);
      console.error('Response:', error.response);
      setAnalyzing(false);
      toast.error(error.response?.data?.detail || t('channelInsights.error_start_analysis'));
    }
  };

  const handleDownload = async (format) => {
    if (!jobId) {
      toast.error(t('channelInsights.no_analysis_export'));
      return;
    }

    // Evita cliques duplos
    if (exportLoading === format) {
      return;
    }

    setExportLoading(format);

    try {
      const token = localStorage.getItem('token');
      const url = `${API}/insights/exportar/${jobId}/${format}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 60000 // 60 segundos de timeout
      });

      // Verificar se a resposta e um erro JSON disfarcado de blob
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || t('channelInsights.error_generate_file'));
      }

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const channelName = analysisResult?.channel_info?.title?.replace(/[^a-z0-9]/gi, '_') || 'canal';
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `analise_${channelName}_${timestamp}.${format}`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(t('channelInsights.download_complete', { format: format.toUpperCase() }));
    } catch (error) {
      console.error('Erro detalhado ao baixar:', error);
      console.error('Error response:', error.response);

      if (error.response?.status === 404) {
        toast.error(t('channelInsights.analysis_not_found'));
      } else if (error.response?.status === 400) {
        toast.error(t('channelInsights.analysis_not_ready'));
      } else if (error.code === 'ECONNABORTED') {
        toast.error(t('channelInsights.timeout_error'));
      } else {
        toast.error(error.response?.data?.detail || error.message || t('channelInsights.error_download'));
      }
    } finally {
      setExportLoading(null);
    }
  };

  // Mostrar overlay de bloqueio se nao tiver acesso
  if (accessCheck.loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
        <Header />
        <main className="dp-main-content" style={{
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          transition: 'margin-left 0.3s ease',
          marginTop: '60px',
          flex: 1,
          minWidth: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>&#8987;</div>
            <p style={{ color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>{t('channelInsights.checking_access')}</p>
          </div>
        </main>
      </div>
    );
  }

  // Sem acesso → vitrine de conversão (mesmo padrão do Niche Finder).
  if (!accessCheck.hasAccess) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
        <Header />
        <FeatureUpsellPage
          icon={<BarChart3 size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
          title={t('channelInsights.title')}
          subtitle={t('channelInsights.subtitle')}
          upsell={{
            badge: t('channelInsights.upsell_badge'),
            headline: t('channelInsights.upsell_headline'),
            subhead: t('channelInsights.upsell_subhead'),
            bullets: [
              { Icon: BarChart3, text: t('channelInsights.upsell_bullet_analysis') },
              { Icon: Award, text: t('channelInsights.upsell_bullet_top') },
              { Icon: Package, text: t('channelInsights.upsell_bullet_themes') },
              { Icon: FileSpreadsheet, text: t('channelInsights.upsell_bullet_export') },
            ],
            ctaLabel: t('channelInsights.upsell_cta'),
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        transition: 'margin-left 0.3s ease',
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        {/* PageHeader */}
        <PageHeader
          icon={<BarChart3 size={20} style={{ color: 'var(--dp-blue-500)' }} />}
          title={t('channelInsights.title')}
          description={t('channelInsights.subtitle')}
        >
          {/* If analyzing, could show a status badge */}
        </PageHeader>

        {/* Aviso de API Key removido (Fase 4 da unificação): a análise usa o
            hub global de chaves — o usuário não precisa configurar nada. */}

        {/* Form de Analise */}
        {!analysisResult && (
          <div className="dp-top-accent" style={{
            background: 'var(--dp-bg-card)',
            border: '1px solid var(--dp-border-subtle)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                color: 'var(--dp-text-primary)',
                marginBottom: '8px',
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'var(--dp-font)'
              }}>
                {t('channelInsights.channel_url')}
              </label>
              <input
                type="text"
                value={channelUrl}
                onChange={(e) => setChannelUrl(e.target.value)}
                placeholder={t('channelInsights.channel_url_placeholder')}
                disabled={analyzing}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--dp-border-subtle)',
                  background: 'var(--dp-bg-card)',
                  color: 'var(--dp-text-primary)',
                  fontSize: '15px',
                  fontFamily: 'var(--dp-font)',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.12)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--dp-border-subtle)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                color: 'var(--dp-text-primary)',
                marginBottom: '8px',
                display: 'block',
                fontFamily: 'var(--dp-font)'
              }}>
                {t('channelInsights.video_count')}
              </label>
              <div className="cq-grid-4" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '12px' }}>
                {[50, 100, 200, 300].map((num) => (
                  <button
                    key={num}
                    onClick={() => setQuantity(num)}
                    disabled={analyzing}
                    style={{
                      padding: '16px',
                      borderRadius: '8px',
                      border: `2px solid ${quantity === num ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-border-subtle)'}`,
                      background: quantity === num
                        ? 'rgba(13, 174, 255, 0.08)'
                        : 'var(--dp-bg-card)',
                      cursor: analyzing ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center',
                      fontFamily: 'var(--dp-font)'
                    }}
                  >
                    <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--dp-text-primary)' }}>
                      {num}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>
                      {t('channelInsights.videos_label')}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                color: 'var(--dp-text-primary)',
                marginBottom: '8px',
                display: 'block',
                fontFamily: 'var(--dp-font)'
              }}>
                {t('channelInsights.sort_by')}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                {[
                  { value: 'recent', label: t('channelInsights.sort_recent'), desc: t('channelInsights.sort_recent_desc') },
                  { value: 'views', label: t('channelInsights.sort_popular'), desc: t('channelInsights.sort_popular_desc') }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setOrderBy(option.value)}
                    disabled={analyzing}
                    style={{
                      padding: '16px',
                      borderRadius: '8px',
                      border: `2px solid ${orderBy === option.value ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-border-subtle)'}`,
                      background: orderBy === option.value
                        ? 'rgba(13, 174, 255, 0.08)'
                        : 'var(--dp-bg-card)',
                      cursor: analyzing ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'left',
                      fontFamily: 'var(--dp-font)'
                    }}
                  >
                    <div style={{ fontWeight: '600', color: 'var(--dp-text-primary)', marginBottom: '4px' }}>
                      {option.label}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>
                      {option.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <button
              className="dp-btn-cta"
              onClick={handleStartAnalysis}
              disabled={analyzing}
              style={{ width: '100%' }}
            >
              {analyzing ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t('channelInsights.analyzing')}
                </>
              ) : (
                <>
                  <Zap size={16} />
                  {t('channelInsights.start_analysis')}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        )}

        {/* Progress Bar */}
        {analyzing && (
          <div style={{
            background: 'var(--dp-bg-card)',
            border: '1px solid var(--dp-border-subtle)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <Loader2 size={20} color="var(--dp-blue-500, #0DAEFF)" className="animate-spin" />
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--dp-text-primary)',
                margin: 0,
                fontFamily: 'var(--dp-font)'
              }}>
                {t('channelInsights.analyzing')}
              </h3>
            </div>

            <div style={{
              background: 'var(--dp-border-subtle)',
              borderRadius: '999px',
              height: '12px',
              overflow: 'hidden',
              marginBottom: '12px'
            }}>
              <div style={{
                background: 'linear-gradient(90deg, var(--dp-blue-500, #0DAEFF), #6D28D9)',
                height: '100%',
                width: `${progress}%`,
                transition: 'width 0.3s ease'
              }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>
                {progressMessage}
              </span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                {progress}%
              </span>
            </div>
          </div>
        )}

        {/* Results Dashboard */}
        {analysisResult && (
          <>
            {/* Channel Header */}
            <div className="dp-top-accent" style={{
              background: 'var(--dp-bg-card)',
              border: '1px solid var(--dp-border-subtle)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '24px'
            }}>
              <img
                src={analysisResult.channel_info.thumbnail_url || analysisResult.channel_info.thumbnail}
                alt={analysisResult.channel_info.title}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/100x100/8B5CF6/FFFFFF?text=Canal';
                }}
                style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  border: '3px solid var(--dp-blue-500, #0DAEFF)',
                  objectFit: 'cover'
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: 'var(--dp-text-primary)',
                    margin: 0,
                    fontFamily: 'var(--dp-font)'
                  }}>
                    {analysisResult.channel_info.title}
                  </h2>
                  {analysisResult.channel_info.custom_url && (
                    <span style={{
                      fontSize: '14px',
                      color: 'var(--dp-text-secondary)',
                      background: 'rgba(13, 174, 255, 0.08)',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontFamily: 'var(--dp-font)'
                    }}>
                      {analysisResult.channel_info.custom_url}
                    </span>
                  )}
                </div>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--dp-text-secondary)',
                  margin: '0 0 16px 0',
                  fontFamily: 'var(--dp-font)'
                }}>
                  {analysisResult.channel_info.description?.substring(0, 200)}...
                </p>
                <div style={{ display: 'flex', gap: '24px' }}>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                      {formatNumber(analysisResult.channel_info.subscriber_count || 0)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>{t('channelInsights.subscribers_label')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                      {formatNumber(analysisResult.channel_info.video_count || 0)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>{t('channelInsights.videos_count')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                      {formatNumber(analysisResult.channel_info.view_count || 0)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>{t('channelInsights.total_views')}</div>
                  </div>
                  <a
                    href={`https://youtube.com/channel/${analysisResult.channel_info.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      marginLeft: 'auto',
                      padding: '8px 16px',
                      background: 'var(--dp-blue-500, #0DAEFF)',
                      color: 'white',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      fontFamily: 'var(--dp-font)'
                    }}
                  >
                    {t('channelInsights.open_youtube')}
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            </div>

            {/* KPI Cards - 6 Cards */}
            <div className="cq-grid-3" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '16px',
              marginBottom: '24px'
            }}>
              {[
                { icon: Eye, label: t('channelInsights.avg_views'), value: formatNumber(Math.round(analysisResult.summary.avg_views)), color: 'var(--dp-blue-500, #0DAEFF)' },
                { icon: BarChart3, label: t('channelInsights.median_views'), value: formatNumber(analysisResult.summary.median_views), color: 'var(--dp-blue-500, #0DAEFF)' },
                { icon: ThumbsUp, label: t('channelInsights.engagement_rate'), value: `${analysisResult.summary.avg_engagement.toFixed(2)}%`, color: 'var(--dp-green-500, #34D399)' },
                { icon: PlayCircle, label: t('channelInsights.total_videos_analyzed'), value: formatNumber(analysisResult.summary.total_videos_analyzed), color: 'var(--dp-blue-500, #0DAEFF)' },
                { icon: Clock, label: t('channelInsights.avg_duration'), value: `${Math.round(analysisResult.summary.avg_duration / 60)}${t('channelInsights.min_suffix')}`, color: 'var(--dp-blue-500, #0DAEFF)' },
                { icon: Calendar, label: t('channelInsights.avg_views_per_day'), value: formatNumber(Math.round(analysisResult.summary.avg_views_per_day)), color: 'var(--dp-blue-500, #0DAEFF)' }
              ].map((stat, idx) => (
                <div key={idx} style={{
                  background: 'var(--dp-bg-card)',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'rgba(13, 174, 255, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <stat.icon size={24} color={stat.color} />
                    </div>
                    <div>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                        {stat.value}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', fontWeight: '500', fontFamily: 'var(--dp-font)' }}>
                        {stat.label}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Export Buttons - Grid de 2 com descricao */}
            <div className="dp-top-accent" style={{
              background: 'var(--dp-bg-card)',
              border: '1px solid var(--dp-border-subtle)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--dp-text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--dp-font)'
                }}>
                  {t('channelInsights.export_analysis')}
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: 'var(--dp-text-secondary)',
                  margin: 0,
                  lineHeight: '1.5',
                  fontFamily: 'var(--dp-font)'
                }}>
                  {t('channelInsights.export_tip')}
                </p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '12px' }}>
                {[
                  { format: 'csv', icon: FileSpreadsheet, label: t('channelInsights.export_csv'), desc: t('channelInsights.export_csv_desc') },
                  { format: 'txt', icon: FileText, label: t('channelInsights.export_txt'), desc: t('channelInsights.export_txt_desc') }
                ].map((exp) => (
                  <button
                    key={exp.format}
                    onClick={() => handleDownload(exp.format)}
                    disabled={exportLoading !== null}
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      border: `1px solid ${exportLoading === exp.format ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-border-subtle)'}`,
                      background: exportLoading === exp.format ? 'rgba(13, 174, 255, 0.08)' : 'var(--dp-bg-card)',
                      cursor: exportLoading !== null ? 'not-allowed' : 'pointer',
                      opacity: exportLoading !== null && exportLoading !== exp.format ? 0.5 : 1,
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      textAlign: 'left',
                      fontFamily: 'var(--dp-font)'
                    }}
                    onMouseEnter={(e) => {
                      if (exportLoading === null) {
                        e.currentTarget.style.background = 'rgba(13, 174, 255, 0.08)';
                        e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (exportLoading !== exp.format) {
                        e.currentTarget.style.background = 'var(--dp-bg-card)';
                        e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                      }
                    }}
                  >
                    {exportLoading === exp.format ? (
                      <Loader2 size={24} color="var(--dp-blue-500, #0DAEFF)" className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <exp.icon size={24} color="var(--dp-blue-500, #0DAEFF)" />
                    )}
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--dp-text-primary)' }}>
                        {exportLoading === exp.format ? t('channelInsights.downloading') : exp.label}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>
                        {exp.desc}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* TOP 10 VIDEOS CAMPEOES */}
            {analysisResult.videos && analysisResult.videos.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '800',
                  color: 'var(--dp-text-primary)',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontFamily: 'var(--dp-font)'
                }}>
                  <Award size={28} color="#F59E0B" />
                  {t('channelInsights.top_videos')}
                </h2>
                <div style={{
                  background: 'var(--dp-bg-card)',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}>
                  {analysisResult.videos
                    .sort((a, b) => b.view_count - a.view_count)
                    .slice(0, 10)
                    .map((video, idx) => (
                      <div
                        key={idx}
                        onClick={() => window.open(`https://youtube.com/watch?v=${video.video_id}`, '_blank')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          padding: '16px 20px',
                          borderBottom: idx < 9 ? '1px solid var(--dp-border-subtle)' : 'none',
                          background: idx < 3 ? 'rgba(245, 158, 11, 0.04)' : 'transparent',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(13, 174, 255, 0.04)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = idx < 3 ? 'rgba(245, 158, 11, 0.04)' : 'transparent'}
                      >
                        <div style={{
                          fontSize: '24px',
                          fontWeight: '800',
                          color: idx === 0 ? '#F59E0B' : idx === 1 ? '#94A3B8' : idx === 2 ? '#CD7F32' : 'var(--dp-text-secondary)',
                          minWidth: '40px',
                          textAlign: 'center',
                          fontFamily: 'var(--dp-font)'
                        }}>
                          #{idx + 1}
                        </div>
                        <img
                          src={video.thumbnail_url || video.thumbnail}
                          alt={video.title}
                          style={{
                            width: '120px',
                            height: '68px',
                            borderRadius: '8px',
                            objectFit: 'cover'
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: 'var(--dp-text-primary)',
                            marginBottom: '6px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontFamily: 'var(--dp-font)'
                          }}>
                            {video.title}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>
                            {new Date(video.published_date).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--dp-blue-500, #0DAEFF)', fontFamily: 'var(--dp-font)' }}>
                              {formatNumber(video.view_count)}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>{t('channelInsights.avg_views').toLowerCase()}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{
                              fontSize: '16px',
                              fontWeight: '700',
                              color: video.engagement_rate > 5 ? 'var(--dp-green-500, #34D399)' : 'var(--dp-text-primary)',
                              fontFamily: 'var(--dp-font)'
                            }}>
                              {video.engagement_rate.toFixed(1)}%
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>eng.</div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* INSIGHTS INTELIGENTES */}
            {analysisResult.insights && analysisResult.insights.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '800',
                  color: 'var(--dp-text-primary)',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontFamily: 'var(--dp-font)'
                }}>
                  <Lightbulb size={28} color="#F59E0B" />
                  {t('channelInsights.insights')}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {analysisResult.insights.slice(0, 5).map((insight, idx) => (
                    <div key={idx} style={{
                      background: 'var(--dp-bg-card)',
                      border: '1px solid var(--dp-border-subtle)',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateX(4px)';
                      e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                    }}
                    >
                      <div style={{
                        fontSize: '32px',
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(13, 174, 255, 0.08)',
                        borderRadius: '12px'
                      }}>
                        {insight.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{
                          fontSize: '15px',
                          fontWeight: '600',
                          color: 'var(--dp-text-primary)',
                          margin: 0,
                          fontFamily: 'var(--dp-font)'
                        }}>
                          {insight.message}
                        </p>
                      </div>
                      {insight.type === 'success' && (
                        <div style={{
                          background: 'rgba(52, 211, 153, 0.1)',
                          color: 'var(--dp-green-500, #34D399)',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '700',
                          fontFamily: 'var(--dp-font)'
                        }}>
                          {t('channelInsights.badge_great')}
                        </div>
                      )}
                      {insight.type === 'warning' && (
                        <div style={{
                          background: 'rgba(245, 158, 11, 0.1)',
                          color: 'var(--dp-orange-500, #F59E0B)',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '700',
                          fontFamily: 'var(--dp-font)'
                        }}>
                          {t('channelInsights.badge_warning')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* GRID: TEMAS E FORMATOS */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '16px' : '24px', marginBottom: isMobile ? '20px' : '32px' }}>

              {/* TEMAS SIMPLIFICADOS */}
              {analysisResult.buckets && analysisResult.buckets.length > 0 && (
                <div>
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: '800',
                    color: 'var(--dp-text-primary)',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontFamily: 'var(--dp-font)'
                  }}>
                    <Package size={28} color="var(--dp-blue-500, #0DAEFF)" />
                    {t('channelInsights.content_themes')}
                  </h2>
                  <div style={{
                    background: 'var(--dp-bg-card)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '16px',
                    overflow: 'hidden'
                  }}>
                    {analysisResult.buckets.slice(0, 6).map((bucket, idx) => {
                      const avgViews = analysisResult.summary.avg_views;
                      const diff = ((bucket.median_views - avgViews) / avgViews * 100);
                      const isChampion = idx === 0;

                      return (
                        <div
                          key={idx}
                          style={{
                            padding: '16px 20px',
                            borderBottom: idx < 5 ? '1px solid var(--dp-border-subtle)' : 'none',
                            background: isChampion ? 'rgba(13, 174, 255, 0.04)' : 'transparent',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(13, 174, 255, 0.06)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = isChampion ? 'rgba(13, 174, 255, 0.04)' : 'transparent'}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                                {bucket.name}
                              </div>
                              {isChampion && (
                                <div style={{
                                  background: '#F59E0B',
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  fontFamily: 'var(--dp-font)'
                                }}>
                                  {t('channelInsights.badge_champion')}
                                </div>
                              )}
                            </div>
                            <div style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>
                              {bucket.total_videos} {t('channelInsights.videos_suffix')}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              flex: 1,
                              height: '8px',
                              background: 'var(--dp-border-subtle)',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${Math.min((bucket.median_views / analysisResult.buckets[0].median_views) * 100, 100)}%`,
                                background: 'linear-gradient(90deg, var(--dp-blue-500, #0DAEFF) 0%, var(--dp-green-500, #34D399) 100%)',
                                borderRadius: '4px',
                                transition: 'width 0.3s'
                              }} />
                            </div>
                            <div style={{ minWidth: '80px', textAlign: 'right' }}>
                              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--dp-blue-500, #0DAEFF)', fontFamily: 'var(--dp-font)' }}>
                                {formatNumber(bucket.median_views)}
                              </div>
                              <div style={{
                                fontSize: '11px',
                                color: diff > 0 ? 'var(--dp-green-500, #34D399)' : 'var(--dp-red-500, #EF4444)',
                                fontWeight: '600',
                                fontFamily: 'var(--dp-font)'
                              }}>
                                {diff > 0 ? '+' : ''}{diff.toFixed(0)}% {t('channelInsights.vs_average')}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* FORMATOS DE TITULO SIMPLIFICADOS */}
              {analysisResult.title_patterns && analysisResult.title_patterns.length > 0 && (
                <div>
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: '800',
                    color: 'var(--dp-text-primary)',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontFamily: 'var(--dp-font)'
                  }}>
                    <Target size={28} color="var(--dp-green-500, #34D399)" />
                    {t('channelInsights.title_patterns')}
                  </h2>
                  <div style={{
                    background: 'var(--dp-bg-card)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '16px',
                    overflow: 'hidden'
                  }}>
                    {analysisResult.title_patterns.slice(0, 6).map((pattern, idx) => {
                      const avgViews = analysisResult.summary.avg_views;
                      const diff = ((pattern.median_views - avgViews) / avgViews * 100);

                      return (
                        <div
                          key={idx}
                          style={{
                            padding: '16px 20px',
                            borderBottom: idx < 5 ? '1px solid var(--dp-border-subtle)' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(52, 211, 153, 0.04)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '6px',
                                background: diff > 0 ? 'rgba(52, 211, 153, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px'
                              }}>
                                {diff > 0 ? '\u2713' : '\u2212'}
                              </div>
                              <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                                {pattern.name}
                              </div>
                            </div>
                            <div style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', paddingLeft: '34px', fontFamily: 'var(--dp-font)' }}>
                              {pattern.total_videos} {t('channelInsights.videos_suffix')} - {formatNumber(pattern.median_views)} {t('channelInsights.median_label')}
                            </div>
                          </div>
                          <div style={{
                            padding: '6px 14px',
                            borderRadius: '8px',
                            background: diff > 0 ? 'rgba(52, 211, 153, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: diff > 0 ? 'var(--dp-green-500, #34D399)' : 'var(--dp-text-secondary)',
                            fontFamily: 'var(--dp-font)'
                          }}>
                            {diff > 0 ? '+' : ''}{diff.toFixed(0)}%
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* OPORTUNIDADES DE CRESCIMENTO */}
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: 'var(--dp-text-primary)',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontFamily: 'var(--dp-font)'
              }}>
                <Zap size={28} color="#F59E0B" />
                {t('channelInsights.growth_opportunities')}
              </h2>
              <div className="cq-grid-3" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
                {/* Oportunidade 1: Tema Campeao */}
                {analysisResult.buckets && analysisResult.buckets.length > 0 && (
                  <div style={{
                    background: 'var(--dp-bg-card)',
                    border: '2px solid #10B981',
                    borderRadius: '12px',
                    padding: '24px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(16, 185, 129, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '-20px',
                      right: '-20px',
                      width: '100px',
                      height: '100px',
                      background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
                      borderRadius: '50%'
                    }} />
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '12px',
                      background: 'rgba(16, 185, 129, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                      fontSize: '28px'
                    }}>&#127919;</div>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--dp-text-primary)', marginBottom: '12px', fontFamily: 'var(--dp-font)' }}>
                      {t('channelInsights.opportunity_theme_title')}
                    </h3>
                    <p style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', marginBottom: '16px', lineHeight: '1.5', fontFamily: 'var(--dp-font)' }}>
                      {t('channelInsights.opportunity_theme_desc', { theme: analysisResult.buckets[0].name, percent: ((analysisResult.buckets[0].median_views - analysisResult.summary.avg_views) / analysisResult.summary.avg_views * 100).toFixed(0) })}
                    </p>
                    <div style={{
                      background: 'linear-gradient(135deg, #10B981, #059669)',
                      color: 'white',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '700',
                      textAlign: 'center',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                      fontFamily: 'var(--dp-font)'
                    }}>
                      {t('channelInsights.opportunity_theme_action')}
                    </div>
                  </div>
                )}

                {/* Oportunidade 2: Formato Eficaz */}
                {analysisResult.title_patterns && analysisResult.title_patterns.length > 0 && analysisResult.title_patterns[0].median_views > analysisResult.summary.avg_views && (
                  <div style={{
                    background: 'var(--dp-bg-card)',
                    border: '2px solid #8B5CF6',
                    borderRadius: '12px',
                    padding: '24px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(139, 92, 246, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '-20px',
                      right: '-20px',
                      width: '100px',
                      height: '100px',
                      background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
                      borderRadius: '50%'
                    }} />
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '12px',
                      background: 'rgba(139, 92, 246, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                      fontSize: '28px'
                    }}>&#128221;</div>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--dp-text-primary)', marginBottom: '12px', fontFamily: 'var(--dp-font)' }}>
                      {t('channelInsights.opportunity_title_title')}
                    </h3>
                    <p style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', marginBottom: '16px', lineHeight: '1.5', fontFamily: 'var(--dp-font)' }}>
                      {t('channelInsights.opportunity_title_desc', { pattern: analysisResult.title_patterns[0].name, percent: ((analysisResult.title_patterns[0].median_views - analysisResult.summary.avg_views) / analysisResult.summary.avg_views * 100).toFixed(0) })}
                    </p>
                    <div style={{
                      background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
                      color: 'white',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '700',
                      textAlign: 'center',
                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                      fontFamily: 'var(--dp-font)'
                    }}>
                      {t('channelInsights.opportunity_title_action')}
                    </div>
                  </div>
                )}

                {/* Oportunidade 3: Duracao */}
                {analysisResult.summary.avg_duration && (
                  <div style={{
                    background: 'var(--dp-bg-card)',
                    border: '2px solid #F59E0B',
                    borderRadius: '12px',
                    padding: '24px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(245, 158, 11, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '-20px',
                      right: '-20px',
                      width: '100px',
                      height: '100px',
                      background: 'radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%)',
                      borderRadius: '50%'
                    }} />
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '12px',
                      background: 'rgba(245, 158, 11, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                      fontSize: '28px'
                    }}>&#9201;&#65039;</div>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--dp-text-primary)', marginBottom: '12px', fontFamily: 'var(--dp-font)' }}>
                      {t('channelInsights.opportunity_duration_title')}
                    </h3>
                    <p style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', marginBottom: '16px', lineHeight: '1.5', fontFamily: 'var(--dp-font)' }}>
                      {t('channelInsights.opportunity_duration_desc', { minutes: Math.round(analysisResult.summary.avg_duration / 60) })}
                    </p>
                    <div style={{
                      background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                      color: 'white',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '700',
                      textAlign: 'center',
                      boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                      fontFamily: 'var(--dp-font)'
                    }}>
                      {t('channelInsights.opportunity_duration_action')}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Nova Analise Button */}
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <button
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={() => {
                  setAnalysisResult(null);
                  setChannelUrl('');
                  setJobId(null);
                  setQuantity(100);
                  setOrderBy('recent');
                }}
              >
                <Zap size={14} />
                {t('channelInsights.new_analysis')}
                <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default InsightsPage;
